import { useQuery } from '@tanstack/react-query'
import { PublicUser } from '~/app/api/users/public-user-schema'
import { useUserStore } from '~/app/_store/user-store'
import { isDev, isPreview } from '~/constants/routing'
import { getSystemAuthor, getUserFromSystemAuthor } from '~/lib/system-authors'
import { userSchemaDbToDefault } from '~/app/api/users/user-schema'
import { invariant } from '@epic-web/invariant'
import { Branch, Commit, isBranch } from '@basehub/data/replicache/index'

export type PublicUserData = PublicUser & {
  isPlayground?: boolean
  isMe?: boolean
}

/**
 * fetches user data from api
 * for "me", it'll get it fresh from useClerk.
 * if `null`, will use the current authenticated user
 */
export function useUserData(
  userId: string | null | undefined,
  optionalSupportData?: {
    ref: Branch | Commit | null
    spaceID: string | null
  }
) {
  const optimisticUser = useUserStore((s) => s.optimisticUser)
  const resolvedUserId =
    userId ?? (userId === undefined ? undefined : optimisticUser?.id)

  const isMe = optimisticUser?.id === resolvedUserId
  const isPlayground = resolvedUserId?.startsWith('playground_')

  const me =
    isMe && optimisticUser
      ? ({
          isMe: true,
          isPlayground,
          id: optimisticUser.id,
          email: optimisticUser.email,
          firstName: optimisticUser.firstName,
          lastName: optimisticUser.lastName,
          username: optimisticUser.username,
          bio: optimisticUser.bio,
          imageUrl: optimisticUser.imageUrl,
          joined: optimisticUser.joined,
          lastAnnouncementReceived: optimisticUser.lastAnnouncementReceived,
          lastChangelogSeen: optimisticUser.lastChangelogSeen,
          showReleaseNote: optimisticUser.showReleaseNote,
          preferences: optimisticUser.preferences,
        } satisfies PublicUserData)
      : null

  const query = useQuery<PublicUserData | null>({
    queryKey: getUserDataQueryKey(resolvedUserId, isMe),
    queryFn: async () => {
      // for "me", i ALWAYS use fresh data.
      // cause when i edit my settings, i need to see them updated immediately.
      if (me) return me
      invariant(resolvedUserId, 'resolvedUserId is required')

      // if support data is available and user has no clerk prefix, look for the agent in the headCommit
      // it's important to do this first, since START could be overwritten as a BaseHub custom agent.
      // if there's no match, we continue with the default user retrieval, including looking for system authors
      $getBaseHubAgent: if (
        !resolvedUserId.startsWith('user_') &&
        optionalSupportData?.ref &&
        optionalSupportData.spaceID
      ) {
        const rootBlockId = isBranch(optionalSupportData.ref)
          ? optionalSupportData.ref.headCommit?.rootBlockId
          : optionalSupportData.ref.rootBlockId
        if (!rootBlockId) break $getBaseHubAgent

        const res = await fetch(
          `/api/agents/${encodeURIComponent(optionalSupportData.spaceID)}/${encodeURIComponent(rootBlockId)}/${resolvedUserId}`
        )
        const data = await res.json()
        const parsedUser = PublicUser.safeParse(data)
        if (parsedUser.success) {
          return parsedUser.data
        }
      }

      const systemAuthor = getSystemAuthor(resolvedUserId)
      if (systemAuthor) {
        return PublicUser.parse(
          userSchemaDbToDefault.parse(getUserFromSystemAuthor(systemAuthor))
        )
      }

      const res = await fetch(`/api/users/${resolvedUserId}`)
      const data = await res.json()
      const parsedUser = PublicUser.safeParse(data)
      if (!parsedUser.success) {
        if (isPreview || isDev) return null
        throw new Error(`Invalid user data: ${parsedUser.error.message}`)
      }
      if (!parsedUser.data) {
        throw new Error(`No user data`)
      }

      return parsedUser.data
    },
    enabled: !!resolvedUserId,
    staleTime: me ? 0 : Infinity,
  })

  return query
}

export function getUserDataQueryKey(
  userId: string | undefined,
  isMe?: boolean
) {
  return ['user-api', userId, isMe ?? false]
}
